import { useQuery } from '@apollo/client/react';
import { Badge } from '@optools/components/ui/badge';
import { Card } from '@optools/components/ui/card';
import { Skeleton } from '@optools/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@optools/components/ui/table';
import { PlaybackOverride } from '@optools/services/operator-gateway/overrides/playback/types';
import { ALL_OVERRIDES } from '@optools/services/operator-gateway/overrides/queries';
import { SessionOverride } from '@optools/services/operator-gateway/overrides/session/types';
import { transformPlaybackOverride } from '@optools/utils/overrides/playback';
import { transformSessionOverride } from '@optools/utils/overrides/session';

export interface UserOverridesProps {
  userId: string;
}

export function UserOverrides({ userId }: UserOverridesProps) {
  const { data, loading } = useQuery(ALL_OVERRIDES);

  const existingSessionOverrides = data?.sessionOverrides
    ?.map(transformSessionOverride)
    ?.filter((override) => override.userId === userId);
  const existingPlaybackOverrides = data?.playbackOverrides
    ?.map(transformPlaybackOverride)
    ?.filter((override) => override.userId === userId);

  return (
    <section className="space-y-4">
      <h2 className="text-sm font-bold">Active Overrides</h2>
      <div className="flex flex-wrap gap-4">
        <OverrideCard
          emptyMessage="There are no active session overrides for this user."
          loading={loading}
          overrides={existingSessionOverrides}
          title="Session Overrides"
          type="session"
        />
        <OverrideCard
          emptyMessage="There are no active playback overrides for this user."
          loading={loading}
          overrides={existingPlaybackOverrides}
          title="Playback Overrides"
          type="playback"
        />
      </div>
    </section>
  );
}

type OverrideCardProps = {
  emptyMessage: string;
  loading: boolean;
  title: string;
} & (
  | {
      overrides: SessionOverride[] | undefined;
      type: 'session';
    }
  | {
      type: 'playback';
      overrides: PlaybackOverride[] | undefined;
    }
);

function OverrideCard({ emptyMessage, loading, overrides, title, type }: OverrideCardProps) {
  return (
    <Card className="flex min-h-32 flex-1 flex-col p-4">
      <h3 className="mb-2 text-sm font-bold">{title}</h3>
      {overrides?.length ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Okta ID</TableHead>
              {type === 'playback' && <TableHead>Experience</TableHead>}
              <TableHead>Client Type</TableHead>
              <TableHead>Features</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overrides.map((override) => {
              const { clientType, features, id, userId } = override;
              return (
                <TableRow key={id}>
                  <TableCell>{id}</TableCell>
                  <TableCell>{userId}</TableCell>
                  {'experience' in override && <TableCell>{override.experience}</TableCell>}
                  <TableCell>{clientType}</TableCell>
                  <TableCell className="flex flex-wrap gap-2">
                    {features.map((feature) => (
                      <Badge key={feature} variant="secondary">
                        {feature}
                      </Badge>
                    ))}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : loading ? (
        <Skeleton className="h-24" />
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm">{emptyMessage}</p>
        </div>
      )}
    </Card>
  );
}
